/**
 * Kanban view of a tracker board (monday.com-style).
 *
 * Generic over the tracker's item source: cards are plain BoardCardData
 * (briefs pages map briefs, custom trackers map tracker items). Columns are
 * the options of the board's first Status custom column; dragging a card to
 * another column reports the new status through `onSetStatus`, so each
 * consumer wires its own persistence (briefs mutation / tracker-item PATCH).
 */
import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Button } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { CalendarIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { workspaceUsersQuery, type WorkspaceUser } from '@/entities/project-member';
import type { TrackerRowUser } from '@/entities/tracker';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { EmptyState } from '@/shared/ui';
import { parseLocalDate } from '../../lib/parseLocalDate';
import { toIdList } from '../../lib/people-ids';
import { useRecordUsers } from '../../lib/record-users';
import type { CustomColumn, StatusOption } from '../../lib/useTrackersBoard';
import { AvatarStack } from '../AvatarStack';
import { useCanEditTracker } from '../../lib/tracker-access';

/** Column id for items that have no value set for the status column. */
const NO_STATUS_ID = '__no_status__';

/** Source-agnostic card model. */
export interface BoardCardData {
  id: string;
  title: string;
  /** Secondary line under the title (e.g. project name); tinted. */
  contextLabel?: string;
  /** Accent color for the card stripe + context label. */
  contextColor?: string;
  /** Small chip on the right (e.g. brief type). */
  chipLabel?: string;
  /** The item's custom column values, keyed by column id. */
  values: Record<string, unknown> | undefined;
  /**
   * This card has somewhere to go. Absent means it has, which is every board
   * whose items share one destination; a record board answers per card, since
   * a record can be missing an id its own route needs.
   */
  routable?: boolean;
}

interface KanbanViewProps {
  items: BoardCardData[];
  customColumns: CustomColumn[];
  /**
   * Which status column the lanes are drawn from. Defaults to the board's
   * first, which is right whenever there is only one; a board carrying several
   * (an asset has both a review state and an approval state) says which, so the
   * lanes agree with what the board is grouped by.
   */
  statusColumnId?: string;
  /**
   * Cards can be dragged between lanes. False where the lane field is one the
   * source will not accept a write to, so the drop would be silently discarded
   * and the card would spring back.
   */
  draggable?: boolean;
  /**
   * Narrows `draggable` to the cards whose lane value this caller may change,
   * on a board where that right is per record. Absent means every card.
   */
  isItemDraggable?: (itemId: string) => boolean;
  onItemClick?: (itemId: string) => void;
  /** Persist a status change; `null` clears the value. */
  onSetStatus: (itemId: string, statusId: string | null) => void;
  /**
   * Create an item already carrying the column's status. Boards that cannot
   * create their own items (briefs come from elsewhere) leave it out, and the
   * empty column then offers no Add item.
   */
  onAddItem?: (statusId: string | null, title: string) => void;
  /**
   * A search or filter is narrowing `items`. A lane emptied that way says so
   * and drops its Add item, since a new item carries the lane's status and
   * nothing else, so it would fail the narrowing and never appear.
   */
  filtered?: boolean;
  /**
   * How many items the lane holds in total, for a board whose rows arrive a
   * page at a time. A lane otherwise counts the cards it was handed, which on
   * a paged board is a figure that climbs as the user scrolls.
   */
  laneCount?: (statusId: string | null) => number | undefined;
}

const DEFAULT_ACCENT = '#94a3b8';

/** The columns a card summarises below its title, beyond the group name. */
interface CardFieldColumns {
  dueDate?: CustomColumn;
  owner?: CustomColumn;
}

type CardUser = WorkspaceUser | TrackerRowUser;

function KanbanCard({
  data,
  fieldColumns,
  users,
  onClick,
  dragging,
}: {
  data: BoardCardData;
  fieldColumns: CardFieldColumns;
  users: CardUser[];
  onClick?: () => void;
  dragging?: boolean;
}) {
  const accent = data.contextColor ?? DEFAULT_ACCENT;
  const dueDate = readDate(data, fieldColumns.dueDate);
  const ownerIds = readUserIds(data, fieldColumns.owner);

  return (
    <div
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={`rounded-lg border border-border bg-card p-3 shadow-sm transition-shadow hover:shadow-md ${
        onClick ? 'cursor-pointer ' : ''
      }${dragging ? 'shadow-lg ring-2 ring-ring' : ''}`}
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="text-sm font-medium text-foreground">{data.title}</div>
      {data.contextLabel || data.chipLabel ? (
        <div className="mt-1.5 flex items-center gap-2">
          {data.contextLabel ? (
            <span className="truncate text-xs font-medium" style={{ color: accent }}>
              {data.contextLabel}
            </span>
          ) : null}
          {data.chipLabel ? (
            <span className="ml-auto shrink-0 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
              {data.chipLabel}
            </span>
          ) : null}
        </div>
      ) : null}
      {/* A card with neither a due date nor an owner keeps its old height
         rather than reserving space for a row that will never fill. */}
      {dueDate || ownerIds.length > 0 ? (
        <div className="mt-2 flex min-h-6 items-center gap-2">
          {dueDate ? (
            // Named after the column it came from: the board's first date
            // column may well be "Kickoff", and a bare date next to a calendar
            // icon would read as a deadline.
            <span
              className="flex items-center gap-1 text-xs text-muted-foreground"
              title={fieldColumns.dueDate?.label}
            >
              <CalendarIcon className="size-3.5 shrink-0" aria-hidden />
              <span className="sr-only">{fieldColumns.dueDate?.label}</span>
              {/* eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to format() rather than translator-facing copy */}
              {format(dueDate, 'MMM d, yyyy')}
            </span>
          ) : null}
          {ownerIds.length > 0 ? (
            <div className="ml-auto flex items-center">
              <span className="sr-only">{fieldColumns.owner?.label}</span>
              <AvatarStack ids={ownerIds} users={users} max={2} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function readDate(card: BoardCardData, column: CustomColumn | undefined): Date | undefined {
  if (!column) return undefined;
  const value = card.values?.[column.id];
  return typeof value === 'string' ? parseLocalDate(value) : undefined;
}

function readUserIds(card: BoardCardData, column: CustomColumn | undefined): string[] {
  return column ? toIdList(card.values?.[column.id]) : [];
}

function DraggableCard({
  data,
  fieldColumns,
  users,
  onItemClick,
  draggable,
}: {
  data: BoardCardData;
  fieldColumns: CardFieldColumns;
  users: CardUser[];
  onItemClick?: (itemId: string) => void;
  draggable: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: data.id,
    disabled: !draggable,
  });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className={isDragging ? 'opacity-40' : ''}>
      <KanbanCard
        data={data}
        fieldColumns={fieldColumns}
        users={users}
        onClick={onItemClick && data.routable !== false ? () => onItemClick(data.id) : undefined}
      />
    </div>
  );
}

/**
 * Empty-column prompt. It sits inside the droppable body, so a card can still
 * be dropped onto the column while the prompt is showing. Boards that cannot
 * create items pass no `onAdd` and get the copy alone.
 */
function EmptyColumn({ filtered, onAdd }: { filtered?: boolean; onAdd?: () => void }) {
  const { t } = useTranslation('trackers');

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-2 text-center">
      <p className="text-xs text-muted-foreground">
        {filtered
          ? t('trackersBoard.kanbanView.noItemsMatchTheFilters')
          : t('trackersBoard.kanbanView.nothingHereYet')}
      </p>
      {onAdd ? (
        <Button variant="ghost" size="sm" onClick={onAdd}>
          <PlusIcon className="size-3.5" aria-hidden />
          {t('trackersBoard.kanbanView.addItem')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Title entry for a new card. Enter creates it; Escape and clicking away both
 * abandon it. The input is a transient swap for the button the user pressed,
 * where clicking elsewhere reads as backing out, so committing on blur would
 * leave items behind nobody asked for.
 */
function AddCardInput({
  value,
  onChange,
  onCommit,
  onCancel,
}: {
  value: string;
  onChange: (next: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('trackers');

  return (
    <input
      // eslint-disable-next-line jsx-a11y/no-autofocus -- the input replaces the button the user just pressed, so focus has to follow it
      autoFocus
      value={value}
      aria-label={t('trackersBoard.kanbanView.itemTitle')}
      placeholder={t('trackersBoard.kanbanView.addItem')}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommit();
        if (e.key === 'Escape') onCancel();
      }}
      onBlur={onCancel}
      className="w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
    />
  );
}

function KanbanColumn({
  option,
  cards,
  count,
  fieldColumns,
  users,
  onItemClick,
  onAddItem,
  filtered,
  draggable,
  isItemDraggable,
}: {
  option: StatusOption;
  cards: BoardCardData[];
  count: number;
  fieldColumns: CardFieldColumns;
  users: CardUser[];
  onItemClick?: (itemId: string) => void;
  onAddItem?: (title: string) => void;
  filtered?: boolean;
  draggable: boolean;
  isItemDraggable?: (itemId: string) => boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: option.id });
  // The draft lives on the column, not on the empty prompt: a card landing in
  // the lane while the user types (a refetch, an automation, another client)
  // takes the prompt away, and with it the title they had half-written.
  const [draftTitle, setDraftTitle] = useState<string | null>(null);
  const commitDraft = () => {
    const title = draftTitle?.trim();
    setDraftTitle(null);
    if (title) onAddItem?.(title);
  };

  return (
    <div role="group" aria-label={option.label} className="flex w-72 shrink-0 flex-col">
      <div
        data-testid="kanban-column-header"
        className="flex items-center gap-2 rounded-t-lg border border-b-0 border-border bg-card px-3 py-2 text-sm font-semibold text-foreground"
      >
        <span
          data-testid="kanban-status-dot"
          className="size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: option.color }}
          aria-hidden
        />
        <span className="truncate">{option.label}</span>
        <span className="ml-auto text-xs font-medium text-muted-foreground">{count}</span>
      </div>
      <div
        ref={setNodeRef}
        className={`flex min-h-32 flex-1 flex-col gap-2 rounded-b-lg border border-t-0 border-border p-2 transition-colors ${
          isOver ? 'bg-accent' : 'bg-muted/40'
        }`}
      >
        {cards.map((c) => (
          <DraggableCard
            key={c.id}
            data={c}
            fieldColumns={fieldColumns}
            users={users}
            onItemClick={onItemClick}
            draggable={draggable && (isItemDraggable?.(c.id) ?? true)}
          />
        ))}
        {cards.length === 0 && draftTitle === null ? (
          <EmptyColumn
            filtered={filtered}
            onAdd={onAddItem ? () => setDraftTitle('') : undefined}
          />
        ) : null}
        {draftTitle !== null ? (
          <AddCardInput
            value={draftTitle}
            onChange={setDraftTitle}
            onCommit={commitDraft}
            onCancel={() => setDraftTitle(null)}
          />
        ) : null}
      </div>
    </div>
  );
}

export function KanbanView({
  items,
  customColumns,
  statusColumnId,
  draggable: draggableProp = true,
  isItemDraggable,
  onItemClick,
  onSetStatus,
  onAddItem: onAddItemProp,
  filtered,
  laneCount,
}: KanbanViewProps) {
  const { t } = useTranslation('trackers');
  const canEdit = useCanEditTracker();
  const draggable = canEdit && draggableProp;
  const onAddItem = canEdit ? onAddItemProp : undefined;
  const workspaceId = useCurrentWorkspaceId();

  const statusColumn = useMemo(
    () =>
      customColumns.find(
        (c) => c.type === 'status' && (!statusColumnId || c.id === statusColumnId),
      ),
    [customColumns, statusColumnId],
  );

  const fieldColumns = useMemo<CardFieldColumns>(
    () => ({
      dueDate: customColumns.find((c) => c.type === 'date'),
      owner: customColumns.find((c) => c.type === 'people'),
    }),
    [customColumns],
  );

  const columns = useMemo<Array<{ option: StatusOption; cards: BoardCardData[] }>>(() => {
    if (!statusColumn) return [];
    const options = statusColumn.statusOptions ?? [];
    const byValue = new Map<string, BoardCardData[]>(options.map((o) => [o.id, []]));
    const none: BoardCardData[] = [];
    for (const card of items) {
      const value = card.values?.[statusColumn.id];
      const bucket = typeof value === 'string' ? byValue.get(value) : undefined;
      if (bucket) bucket.push(card);
      else none.push(card);
    }
    const statusColumns = options.map((option) => ({
      option,
      cards: byValue.get(option.id) ?? [],
    }));
    // An always-present "No status" lane is a column the board's own statuses
    // do not define, so it only earns its width while it holds something. The
    // `null` status paths below and in handleDragEnd are what that lane feeds,
    // so with it gone a status is cleared from the table view instead.
    if (none.length === 0) return statusColumns;
    return [
      ...statusColumns,
      {
        option: {
          id: NO_STATUS_ID,
          label: t('trackersBoard.kanbanView.noStatus'),
          color: DEFAULT_ACCENT,
        },
        cards: none,
      },
    ];
  }, [statusColumn, items, t]);

  // Record users first, for the reason given on the people cell in
  // `CustomCells/ReadOnlyCell`; the roster is asked only for whoever is left.
  const recordUsers = useRecordUsers();
  const rosterNeeded = useMemo(
    () =>
      items.some((card) =>
        readUserIds(card, fieldColumns.owner).some((id) => !recordUsers.has(id)),
      ),
    [items, fieldColumns.owner, recordUsers],
  );
  const usersQuery = useQuery({
    ...workspaceUsersQuery(workspaceId),
    enabled: Boolean(workspaceId) && rosterNeeded,
  });
  const users = useMemo<CardUser[]>(
    () => [...recordUsers.values(), ...(usersQuery.data?.data ?? [])],
    [recordUsers, usersQuery.data],
  );

  const [activeCard, setActiveCard] = useState<BoardCardData | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = (event: DragStartEvent) => {
    setActiveCard(items.find((c) => c.id === event.active.id) ?? null);
  };

  const noCardMovable =
    draggable && isItemDraggable !== undefined && !items.some((c) => isItemDraggable(c.id));

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveCard(null);
    const { active, over } = event;
    if (!over || !statusColumn) return;
    const card = items.find((c) => c.id === active.id);
    if (!card) return;
    const currentValue = card.values?.[statusColumn.id];
    const nextValue = over.id === NO_STATUS_ID ? null : String(over.id);
    if ((currentValue ?? null) === nextValue) return;
    onSetStatus(card.id, nextValue);
  };

  if (!statusColumn) {
    return (
      <EmptyState
        className="min-h-40"
        title={t('trackersBoard.kanbanView.noStatusColumnYet')}
        description={
          canEdit
            ? t('trackersBoard.kanbanView.addAStatusColumnIn')
            : t('trackersBoard.viewOnly.noStatusColumnDescription')
        }
      />
    );
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      {/* A card that will not pick up reads as a broken board. Say which column
          the lanes are drawn from and that its value is not this board's to set,
          rather than leaving the person dragging at it. */}
      {!draggable ? (
        <p className="pb-3 text-xs text-muted-foreground">
          {canEdit
            ? t('trackersBoard.kanbanView.cardsCannotBeMovedBetween', {
                column: statusColumn.label,
              })
            : t('trackersBoard.viewOnly.boardNotice')}
        </p>
      ) : noCardMovable && items.length > 0 ? (
        <p className="pb-3 text-xs text-muted-foreground">
          {t('trackersBoard.kanbanView.cardsReadOnlyForYou', { column: statusColumn.label })}
        </p>
      ) : null}
      <div className="flex gap-3 overflow-x-auto pb-3">
        {columns.map(({ option, cards: columnCards }) => (
          <KanbanColumn
            key={option.id}
            option={option}
            cards={columnCards}
            count={laneCount?.(option.id === NO_STATUS_ID ? null : option.id) ?? columnCards.length}
            fieldColumns={fieldColumns}
            users={users}
            onItemClick={onItemClick}
            onAddItem={
              onAddItem && !filtered
                ? (title) => onAddItem(option.id === NO_STATUS_ID ? null : option.id, title)
                : undefined
            }
            filtered={filtered}
            draggable={draggable}
            isItemDraggable={isItemDraggable}
          />
        ))}
      </div>
      {/* dropAnimation={null}: the default drop animation glides the overlay
         back to the card's ORIGINAL position — but our optimistic mutation has
         already moved the card to the target column, so that glide reads as
         "the card snapped back". Disable it so the drop lands instantly. */}
      <DragOverlay dropAnimation={null}>
        {activeCard ? (
          <KanbanCard data={activeCard} fieldColumns={fieldColumns} users={users} dragging />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
